import { useCallback, useRef, useState } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import { toast } from "react-toastify";
import TelemetryService from "../shared/services/TelemetryService";

interface FFmpegState {
  isLoading: boolean;
  isLoaded: boolean;
  isCompressing: boolean;
  progress: number;
  error: string | null;
}

interface CompressionResult {
  success: boolean;
  blob?: Blob;
  error?: string;
  originalSize: number;
  compressedSize?: number;
}

const DEFAULT_COMPRESSION_CONFIG = {
  maxWidth: 1280,
  maxHeight: 720,
  targetSize: 10, // MB
  crf: 23,
  preset: "veryfast",
};

// Configurações específicas para iOS (iPhone)
const IOS_COMPRESSION_CONFIG = {
  maxWidth: 640,
  maxHeight: 480,
  targetSize: 5, // MB - metade do padrão
  crf: 32, // Mais alto = mais compressão
  preset: "veryfast",
};

// Detecta se é um dispositivo iOS
const isIOS = () => {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

export const useFFmpeg = (correlationId: string) => {
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const [state, setState] = useState<FFmpegState>({
    isLoading: false,
    isLoaded: false,
    isCompressing: false,
    progress: 0,
    error: null,
  });

  const loadFFmpeg = useCallback(async () => {
    if (state.isLoaded) return;

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd";
      const ffmpeg = new FFmpeg();
      ffmpegRef.current = ffmpeg;

      // Configurar logs e progresso
      ffmpeg.on("log", ({ message }) => {
        console.log("[FFmpeg]:", message);
        TelemetryService.info(
          "useFFmpeg",
          "ffmpeg_log",
          { message },
          correlationId
        );
      });

      await ffmpeg.load({
        coreURL: await toBlobURL(
          `${baseURL}/ffmpeg-core.js`,
          "text/javascript"
        ),
        wasmURL: await toBlobURL(
          `${baseURL}/ffmpeg-core.wasm`,
          "application/wasm"
        ),
      });

      setState((prev) => ({
        ...prev,
        isLoaded: true,
        isLoading: false,
      }));

      TelemetryService.info(
        "useFFmpeg",
        "core_loaded",
        { success: true },
        correlationId
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erro desconhecido ao carregar FFmpeg";
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      TelemetryService.error(
        "useFFmpeg",
        "core_load_error",
        { error: errorMessage },
        correlationId
      );

      toast.error(
        "Erro ao inicializar o processador de vídeo. Por favor, tente novamente."
      );
    }
  }, [state.isLoaded, correlationId]);

  const compressVideo = useCallback(
    async (
      inputBlob: Blob,
      config = DEFAULT_COMPRESSION_CONFIG
    ): Promise<CompressionResult> => {
      // Compressão desabilitada - sempre retorna o vídeo original
      TelemetryService.info(
        "useFFmpeg",
        "ffmpeg_log",
        {
          message: "Compressão de vídeo desabilitada - retornando vídeo original",
          originalSize: inputBlob.size / (1024 * 1024),
        },
        correlationId
      );

      return {
        success: true,
        blob: inputBlob,
        originalSize: inputBlob.size / (1024 * 1024),
        compressedSize: inputBlob.size / (1024 * 1024),
      };
    },
    [correlationId]
  );

  return {
    ...state,
    loadFFmpeg,
    compressVideo,
  };
};

export default useFFmpeg;
